import { friendlyColorName, parseColors, parseSizes } from './productUtils';

// Apparel sizes in wearing order; anything else (numeric waist sizes, "500ML") sorts
// numerically, then alphabetically, after them.
const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', 'XXXL', '3XL', '4XL', '5XL', 'FREE SIZE', 'ONE SIZE'];
export function compareSizes(a, b) {
  const ia = SIZE_ORDER.indexOf(String(a).toUpperCase());
  const ib = SIZE_ORDER.indexOf(String(b).toUpperCase());
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  const na = parseFloat(a), nb = parseFloat(b);
  if (!isNaN(na) && !isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b));
}

/** Normalises the three ways a product can carry options into one shape:
 *  - 'combo': variant rows with colour × size
 *  - 'label': variant rows with a single label ("500ML")
 *  - 'simple': the plain comma-separated colors/sizes strings, no variant rows
 *  Sold-out choices are kept (flagged unavailable) so they can be shown crossed out. */
export function getOptions(product) {
  if (!product) return { kind: 'none', colors: [], sizesFor: () => [], hasSize: false };
  const variants = product.variants || [];
  const isAvailable = (v) => v.available !== false;

  if (variants.some(v => v.color != null)) {
    const colorMap = new Map();
    variants.filter(v => v.color).forEach(v => {
      const prev = colorMap.get(v.color);
      colorMap.set(v.color, {
        name: v.color,
        label: friendlyColorName(v.color, v.colorHex || v.color),
        hex: v.colorHex || v.color,
        image: prev?.image || v.imageUrl || null,
        available: (prev?.available || false) || isAvailable(v),
      });
    });
    const hasSize = variants.some(v => v.size != null);
    return {
      kind: 'combo', hasSize,
      colors: [...colorMap.values()],
      sizesFor: (color) => variants
        .filter(v => v.color === color && v.size)
        .map(v => ({ value: v.size, available: isAvailable(v) }))
        .sort((a, b) => compareSizes(a.value, b.value)),
      find: (color, size) => variants.find(v => v.color === color && (!hasSize || v.size === size)) || null,
    };
  }
  if (variants.length) {
    return {
      kind: 'label', hasSize: true, colors: [],
      sizesFor: () => variants.map(v => ({ value: v.label, available: isAvailable(v) })),
      find: (_color, size) => variants.find(v => v.label === size) || null,
    };
  }
  const sizes = parseSizes(product.sizes).sort(compareSizes);
  return {
    kind: 'simple', hasSize: sizes.length > 0,
    colors: parseColors(product.colors).map(c => ({ ...c, label: c.name, available: true })),
    sizesFor: () => sizes.map(s => ({ value: s, available: true })),
    find: () => null,
  };
}

/** Everything sold in this product, for listing filters: in-stock sizes and colour names. */
export function availableFacets(product) {
  const o = getOptions(product);
  const colors = o.colors.filter(c => c.available).map(c => c.label);
  const sizes = o.kind === 'combo'
    ? [...new Set(o.colors.flatMap(c => o.sizesFor(c.name).filter(s => s.available).map(s => s.value)))]
    : o.sizesFor().filter(s => s.available).map(s => s.value);
  return { colors, sizes };
}

/** Bag line fields for a choice — label variants carry their value as `label`, not `size`. */
export function lineFor(product, options, color, size) {
  const variant = options.find(color, size);
  return {
    productId: product.id,
    variantId: variant?.id ?? null,
    color: color || null,
    size: options.kind === 'label' ? null : (size || null),
    label: options.kind === 'label' ? size : null,
    sku: variant?.sku || null,
    stock: variant?.stockQty != null ? Number(variant.stockQty) : product.itemsAvailable,
  };
}
